import React from 'react';
import {
  BASE_PROPERTIES,
  COST_PROPERTIES,
  getNumericValue,
  OPTIONS_PROPERTIES,
} from '../../recommendationPropertyUtils';
import RecommendedHourlyCommitment from '../../recommendationGenericComponents/recommendedHourlyCommitment';
import RecommendationPreferences from '../../recommendationGenericComponents/recommendationPreferences';
import RecommendationDetailsLayout from '../../recommendationDetailsLayout';
import RecommendationDetails from '../../recommendationGenericComponents/recommendationDetails';
import RecommendationInsideLook from '../../recommendationGenericComponents/recommendationInsideLook';
import RecommendationCommand, { COMMAND_TYPES } from '../../recommendationGenericComponents/recommendationCommand';
import { useRootStore } from 'app/contexts/RootStoreContext';
import RecommendationOptionsPanel from '~/recommendationsNew/components/detailedRecommendation/recommendationGenericComponents/recommendationOptionsPanel.jsx';

const AZURE_VM_SAVINGS_PLAN_PROPERTIES = {
  TERMS: {
    label: 'Term',
    getterFunction: (recommendation) => recommendation?.recData?.term,
  },
  RECOMMENDED_HOURLY_COMMITMENT: {
    label: 'Hourly Commitment',
    isCurrency: true,
    getterFunction: (recommendation, optionIndex) => {
      let hourlyCommitment = recommendation?.recData?.hourly_commitment;
      if (optionIndex >= 0 && recommendation?.recData?.alternatives) {
        hourlyCommitment = recommendation.recData.alternatives[optionIndex]?.hourly_commitment;
      }
      return getNumericValue(hourlyCommitment)?.toLocaleString(undefined, { maximumFractionDigits: 2 });
    },
  },
  AGE: {
    label: 'Age',
    getterFunction: (recommendation) => recommendation?.age,
  },
  EXPECTED_UTILIZATION: {
    isPercent: true,
    isNoColorInOptions: true,
    label: 'Expected Utilization',
    getterFunction: (recommendation, optionIndex) => {
      let averageUtilizationPercentage = recommendation?.recData?.averageUtilizationPercentage;
      if (optionIndex >= 0 && recommendation?.recData?.alternatives) {
        averageUtilizationPercentage = recommendation.recData.alternatives[optionIndex]?.averageUtilizationPercentage;
      }
      return getNumericValue(averageUtilizationPercentage)?.toLocaleString(undefined, { maximumFractionDigits: 2 });
    },
  },
  EXPECTED_COVERAGE_PERCENTAGE: {
    isPercent: true,
    isNoColorInOptions: true,
    label: 'Expected Coverage Percentage',
    getterFunction: (recommendation, optionIndex) => {
      let coveragePercentage = recommendation?.recData?.coverage_percentage;
      if (optionIndex >= 0 && recommendation?.recData?.alternatives) {
        coveragePercentage = recommendation.recData.alternatives[optionIndex]?.coverage_percentage;
      }
      return getNumericValue(coveragePercentage)?.toLocaleString(undefined, { maximumFractionDigits: 2 });
    },
  },
};

const CUSTOMER_PROPERTY = {
  label: 'Customer',
  getterFunction: (recommendation) => recommendation?.customer,
};

const AzureVmSavingPlan = ({ recommendation }) => {
  const { usersStore } = useRootStore();
  let properties = {
    ...BASE_PROPERTIES,
    ...COST_PROPERTIES,
    ...AZURE_VM_SAVINGS_PLAN_PROPERTIES,
  };
  if (usersStore?.isCurrentUserReCustomer) {
    properties = { ...properties, CUSTOMER_PROPERTY };
  }

  const description =
    'Based on your on-demand usage cost, we recommend committing to this Savings Plan to optimize costs and reduce your hourly rate for eligible resources.';
  const descriptionComponent = <RecommendationInsideLook description={description} />;

  const commandsList = [
    {
      type: COMMAND_TYPES.CONSOLE,
      instructions: [
        {
          actionText:
            'https://learn.microsoft.com/en-us/cli/azure/billing-benefits/savings-plan-order?view=azure-cli-latest',
          actionLink:
            'https://learn.microsoft.com/en-us/cli/azure/billing-benefits/savings-plan-order?view=azure-cli-latest',
        },
      ],
    },
  ];
  const commandComponent = <RecommendationCommand commandsList={commandsList} />;

  const detailsComponent = (
    <RecommendationDetails recommendationProperties={properties} recommendation={recommendation} />
  );

  const preferencesComponent = <RecommendationPreferences recommendationType={recommendation?.typeId} />;

  const hourlyCommitmentComponent = (
    <RecommendedHourlyCommitment hourlyCommitment={recommendation?.recData?.hourly_commitment} />
  );

  const CURRENT_PROPERTIES = [properties.CURRENT_ANNUAL_COST];

  const ALTERNATIVE_PROPERTIES = [
    properties.RECOMMENDED_HOURLY_COMMITMENT,
    properties.RECOMMENDED_ANNUAL_COST,
    OPTIONS_PROPERTIES.POTENTIAL_SAVINGS,
    properties.EXPECTED_UTILIZATION,
    properties.EXPECTED_COVERAGE_PERCENTAGE,
  ];

  const alternatives = recommendation?.recData?.alternatives;
  const optionsPanelComponent =
    Array.isArray(alternatives) && alternatives.length ? (
      <RecommendationOptionsPanel
        alternatives={alternatives}
        currentProperties={CURRENT_PROPERTIES}
        alternativeProperties={ALTERNATIVE_PROPERTIES}
        recommendation={recommendation}
        isRecommendedOption
        minHeight={125}
      />
    ) : null;

  return (
    <RecommendationDetailsLayout
      command={commandComponent}
      details={detailsComponent}
      preferences={preferencesComponent}
      insideLook={descriptionComponent}
      hourlyCommitment={hourlyCommitmentComponent}
      optionsPanel={optionsPanelComponent}
    />
  );
};

export default AzureVmSavingPlan;
